import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useProfile } from '../../../hooks/useProfile';
import { useUnreadComments } from '../../../hooks/useUnreadComments';
import { supabase } from '../../../lib/supabase';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import ProfileHeader from './components/ProfileHeader';
import StatsRow from './components/StatsRow';
import StoriesTab from './components/StoriesTab';

interface StoryRow {
  id: string;
  title: string;
  genre: string;
  status: string;
  read_count: number;
  like_count: number;
  published_date: string | null;
  updated_at: string;
}

export default function WriterProfilePage() {
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile(user?.id || null);
  const { unreadCount, unreadComments, markAsRead } = useUnreadComments(user?.id || null);
  const [stories, setStories] = useState<StoryRow[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('stories')
      .select('id, title, genre, status, read_count, like_count, published_date, updated_at')
      .eq('author_id', user.id)
      .order('updated_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setStories(data as StoryRow[]);
      });
  }, [user?.id]);

  const handleAvatarUpdate = async (url: string) => {
    await updateProfile({ avatar_url: url });
  };

  const handleUsernameUpdate = async (username: string) => {
    await updateProfile({ username });
  };

  const mappedStories = stories.map((s) => ({
    id: s.id,
    title: s.title,
    genre: s.genre,
    datePublished: s.published_date ?? s.updated_at,
    readCount: s.read_count ?? 0,
    likeCount: s.like_count ?? 0,
    status: s.status,
  }));

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12 pt-28">
          <div className="animate-pulse">
            <div className="h-48 bg-zinc-900 rounded-lg mb-8"></div>
            <div className="grid grid-cols-4 gap-6 mb-12">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-zinc-900 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-zinc-900 rounded-lg"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Profile not found</h2>
            <p className="text-zinc-400">Unable to load profile data.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 pt-28">
        <ProfileHeader
          profile={{
            displayName: profile.display_name,
            username: profile.username,
            bio: profile.bio || '',
            avatarUrl: profile.avatar_url || '',
            joinDate: profile.join_date,
            role: 'writer',
          }}
          onEditClick={() => {}}
          onAvatarUpdate={handleAvatarUpdate}
          onUsernameUpdate={handleUsernameUpdate}
          unreadCount={unreadCount}
          unreadComments={unreadComments}
          onMarkAsRead={markAsRead}
        />

        <StatsRow
          stats={{
            totalStories: mappedStories.filter((s) => s.status === 'published').length,
            totalReads: mappedStories.reduce((sum, s) => sum + s.readCount, 0),
            totalLikes: mappedStories.reduce((sum, s) => sum + s.likeCount, 0),
            followers: (profile as any).followers ?? 0,
          }}
        />

        <StoriesTab stories={mappedStories} />
      </main>

      <Footer />
    </div>
  );
}
